import { KeyRound, Plus, Save, Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import type { BillingRates, ModelPrice } from "@navibiz/chat-core";
import { api } from "../../lib/api";
import { ROLE_LABEL, when } from "../../lib/format";
import { keys, useAction, useAdmins, useAudit, useBilling, useMe, useOverview } from "../../lib/queries";
import type { Role } from "../../lib/types";
import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { Dialog } from "../../ui/Dialog";
import { ErrorState, PageSkeleton, useToast } from "../../ui/Feedback";
import { Field, FormGrid, Input, Select } from "../../ui/Form";
import { CodeBlock, PageHeader } from "../../ui/Misc";
import table from "../../ui/Table.module.css";
import styles from "./SettingsPage.module.css";

export function SettingsPage() {
  const me = useMe().data!;
  if (me.role === "client") {
    return (
      <div className={styles.page}>
        <PageHeader kicker="Akun" title="Akun saya" description="Ganti kata sandi Console Anda di sini." />
        <AccountCard />
      </div>
    );
  }
  return (
    <div className={styles.page}>
      <PageHeader kicker="Pengaturan" title="Tarif, tim, dan jejak audit" description="Berlaku untuk semua proyek. Setiap perubahan tercatat di audit log." />
      <BillingCard canEdit={me.role === "admin" || me.role === "finance"} />
      <AccountCard />
      {me.role === "admin" && <TeamCard />}
      {me.role === "admin" && <AuditCard />}
    </div>
  );
}

function BillingCard({ canEdit }: { canEdit: boolean }) {
  const billing = useBilling();
  const toast = useToast();
  const [prices, setPrices] = useState<ModelPrice[]>([]);
  const [rates, setRates] = useState<BillingRates>({ idrPerUsd: 0, providerFactor: 1, saldoFactor: 1.5 });
  useEffect(() => {
    if (billing.data) {
      setPrices(billing.data.prices);
      setRates(billing.data.rates);
    }
  }, [billing.data]);
  const save = useAction((body: { prices: ModelPrice[]; rates: BillingRates }) => api.put("/settings/billing", body), [keys.billing]);

  if (billing.isPending) return <PageSkeleton />;
  if (billing.isError) return <ErrorState error={billing.error} />;
  const update = (i: number, patch: Partial<ModelPrice>) => setPrices(prices.map((price, j) => (j === i ? { ...price, ...patch } : price)));
  // A typical conversation: 5 answers × (≈2.300 input + 160 output tokens), uncached. Linkage adds
  // ≈400 input tokens of candidates and ≈45 output tokens of picks per answer.
  const perConversation = (price: ModelPrice, linkage = false) =>
    ((5 * ((2300 + (linkage ? 400 : 0)) * price.inputPerMTok + (160 + (linkage ? 45 : 0)) * price.outputPerMTok)) / 1_000_000) * rates.idrPerUsd * rates.saldoFactor;

  return (
    <Card
      title="Tarif penyedia AI"
      description="Saldo dipotong: token × harga resmi (USD/1 juta token) × kurs JISDOR × faktor harga saldo. Selisihnya dengan faktor biaya penyedia adalah pendapatan Navibiz. Tinjau tiap kuartal."
      actions={
        canEdit && (
          <Button
            variant="primary"
            icon={<Save aria-hidden />}
            loading={save.isPending}
            onClick={async () => {
              await save.mutateAsync({ prices, rates });
              toast("Tarif disimpan. Berlaku untuk jawaban berikutnya (maks. 1 menit).");
            }}
          >
            Simpan tarif
          </Button>
        )
      }
    >
      <FormGrid columns={3}>
        <Field label="Kurs acuan (Rp per USD)" hint="Kurs tengah BI (JISDOR).">
          {(id, describedBy) => <Input id={id} type="number" disabled={!canEdit} value={rates.idrPerUsd} onChange={(e) => setRates({ ...rates, idrPerUsd: Number(e.target.value) })} aria-describedby={describedBy} />}
        </Field>
        <Field label="Faktor biaya penyedia" hint="1,15: biaya Navibiz yang sebenarnya (kurs, PPN jasa digital LN, kartu). Untuk pembukuan, bukan untuk saldo.">
          {(id, describedBy) => (
            <Input id={id} type="number" step="0.01" min={1} max={2} disabled={!canEdit} value={rates.providerFactor} onChange={(e) => setRates({ ...rates, providerFactor: Number(e.target.value) })} aria-describedby={describedBy} />
          )}
        </Field>
        <Field label="Faktor harga saldo" hint="1,5 sejak katalog v4: pengali saat memotong saldo klien (Lampiran III). Pengganti biaya layanan top-up.">
          {(id, describedBy) => (
            <Input id={id} type="number" step="0.01" min={1} max={3} disabled={!canEdit} value={rates.saldoFactor} onChange={(e) => setRates({ ...rates, saldoFactor: Number(e.target.value) })} aria-describedby={describedBy} />
          )}
        </Field>
      </FormGrid>

      <div className={table.wrap} style={{ marginTop: 20 }}>
        <table className={table.table}>
          <thead>
            <tr>
              <th>Model</th>
              <th>Label</th>
              <th className={table.right}>Input</th>
              <th className={table.right}>Output</th>
              <th className={table.right}>Cache write</th>
              <th className={table.right}>Cache read</th>
              <th className={table.right}>≈ per percakapan</th>
              <th className={table.right}>+ Linkage</th>
            </tr>
          </thead>
          <tbody>
            {prices.map((price, i) => (
              <tr key={price.model}>
                <td>
                  <code>{price.model}</code>
                  {price.reasoningEffort && <small style={{ display: "block", color: "var(--ink-3)" }}>reasoning: {price.reasoningEffort}</small>}
                </td>
                <td>
                  <Input value={price.label} disabled={!canEdit} onChange={(e) => update(i, { label: e.target.value })} aria-label={`Label ${price.model}`} />
                </td>
                {(["inputPerMTok", "outputPerMTok", "cacheWritePerMTok", "cacheReadPerMTok"] as const).map((field) => (
                  <td key={field} className={table.right}>
                    <Input
                      type="number"
                      step="0.001"
                      min={0}
                      disabled={!canEdit}
                      value={price[field]}
                      onChange={(e) => update(i, { [field]: Number(e.target.value) })}
                      aria-label={`${field} ${price.model}`}
                      className={styles.priceInput}
                    />
                  </td>
                ))}
                <td className={table.right}>Rp {Math.round(perConversation(price)).toLocaleString("id-ID")}</td>
                <td className={table.right}>Rp {Math.round(perConversation(price, true)).toLocaleString("id-ID")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={styles.note}>
        Harga dalam USD per 1 juta token, sesuai daftar harga resmi penyedia. Estimasi percakapan: 5 jawaban tanpa cache, dipotong dari saldo. Angka yang disebut ke
        klien tetap batas atas katalog: Rp 300 per percakapan, Rp 360 dengan Linkage.
      </p>
    </Card>
  );
}

function AccountCard() {
  const me = useMe().data!;
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const change = useAction((body: { current: string; next: string }) => api.post("/auth/password", body));

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setError(null);
    if (form.get("next") !== form.get("confirm")) return setError("Konfirmasi kata sandi tidak sama.");
    try {
      await change.mutateAsync({ current: String(form.get("current")), next: String(form.get("next")) });
      formElement.reset();
      toast("Kata sandi diganti. Perangkat lain sudah dikeluarkan.");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Gagal mengganti kata sandi.");
    }
  }

  return (
    <Card title="Akun saya" description={`${me.name} · ${me.email} · ${ROLE_LABEL[me.role]}`}>
      <form onSubmit={submit}>
        <FormGrid>
          <Field label="Kata sandi saat ini">{(id) => <Input id={id} name="current" type="password" autoComplete="current-password" required />}</Field>
          <Field label="Kata sandi baru" hint="Minimal 8 karakter.">
            {(id, describedBy) => <Input id={id} name="next" type="password" autoComplete="new-password" minLength={8} required aria-describedby={describedBy} />}
          </Field>
          <Field label="Ulangi kata sandi baru">{(id) => <Input id={id} name="confirm" type="password" autoComplete="new-password" minLength={8} required />}</Field>
        </FormGrid>
        {error && (
          <p role="alert" style={{ marginTop: 12, color: "var(--critical-ink)" }}>
            {error}
          </p>
        )}
        <div style={{ marginTop: 16 }}>
          <Button type="submit" variant="primary" loading={change.isPending}>
            Ganti kata sandi
          </Button>
        </div>
      </form>
    </Card>
  );
}

function TeamCard() {
  const admins = useAdmins();
  const me = useMe().data!;
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const invalidate = [keys.admins];
  const setRole = useAction(({ id, role }: { id: string; role: Role }) => api.patch(`/settings/admins/${id}`, { role }), invalidate);
  const remove = useAction((id: string) => api.delete(`/settings/admins/${id}`), invalidate);
  const create = useAction((body: Record<string, unknown>) => api.post("/settings/admins", body), invalidate);
  const reset = useAction((id: string) => api.post<{ password: string }>(`/settings/admins/${id}/reset-password`));
  const [issued, setIssued] = useState<{ name: string; email: string; password: string } | null>(null);

  const [newRole, setNewRole] = useState<Role>("specialist");
  const projects = useOverview(30).data?.projects ?? [];

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const body = Object.fromEntries(form);
    await create.mutateAsync({ ...body, projectId: body.role === "client" ? body.projectId : null });
    toast(body.role === "client" ? "Akun klien dibuat. Kirim email dan kata sandi awalnya lewat kanal aman." : "Anggota tim ditambahkan.");
    setAdding(false);
  }

  return (
    <Card
      title="Tim"
      description="Admin: semua. AI Specialist: pengetahuan, Linkage, uji, percakapan. Keuangan: saldo dan tarif. Klien: satu proyeknya sendiri (persona & tampilan widget, playground, percakapan, saldo)."
      flush
      actions={
        <Button size="sm" icon={<Plus aria-hidden />} onClick={() => setAdding(true)}>
          Tambah
        </Button>
      }
    >
      <div className={table.wrap}>
        <table className={table.table}>
          <thead>
            <tr>
              <th className={table.first}>Nama</th>
              <th>Peran</th>
              <th>Terakhir masuk</th>
              <th className={table.last} />
            </tr>
          </thead>
          <tbody>
            {(admins.data ?? []).map((admin) => (
              <tr key={admin.id}>
                <td className={table.first}>
                  <div style={{ fontWeight: 600 }}>
                    {admin.name} {admin.id === me.id && <Badge>Anda</Badge>}
                  </div>
                  <div className={table.muted}>
                    {admin.email}
                    {admin.projectName ? ` · ${admin.projectName}` : ""}
                  </div>
                </td>
                <td>
                  <Select value={admin.role} disabled={admin.id === me.id || admin.role === "client"} onChange={(e) => setRole.mutate({ id: admin.id, role: e.target.value as Role })} aria-label={`Peran ${admin.name}`} style={{ maxWidth: 200 }}>
                    {Object.entries(ROLE_LABEL)
                      .filter(([value]) => value !== "client" || admin.role === "client")
                      .map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                  </Select>
                </td>
                <td className={table.muted}>{when.ago(admin.lastLoginAt)}</td>
                <td className={table.last}>
                  {admin.id !== me.id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Reset kata sandi ${admin.name}`}
                      title="Reset kata sandi"
                      icon={<KeyRound aria-hidden />}
                      loading={reset.isPending && reset.variables === admin.id}
                      onClick={async () => {
                        if (!confirm(`Buat kata sandi baru untuk ${admin.name}? Kata sandi lama langsung tidak berlaku dan semua sesinya keluar.`)) return;
                        const { password } = await reset.mutateAsync(admin.id);
                        setIssued({ name: admin.name, email: admin.email, password });
                      }}
                    />
                  )}
                  {admin.id !== me.id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Hapus ${admin.name}`}
                      icon={<Trash2 aria-hidden />}
                      onClick={() => confirm(`Hapus akses ${admin.name}?`) && remove.mutate(admin.id)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {reset.isError && <p style={{ margin: "12px 20px", color: "var(--critical-ink)" }}>{reset.error.message}</p>}
      <Dialog
        open={!!issued}
        onClose={() => setIssued(null)}
        title="Kata sandi baru"
        description={issued ? `${issued.name} · ${issued.email}` : undefined}
        footer={
          <Button variant="primary" onClick={() => setIssued(null)}>
            Selesai
          </Button>
        }
      >
        {issued && (
          <div style={{ display: "grid", gap: 12 }}>
            <CodeBlock code={issued.password} />
            <p style={{ margin: 0, color: "var(--ink-2)" }}>
              Hanya ditampilkan sekali. Kirim lewat kanal pribadi (WhatsApp atau telepon langsung), lalu minta yang bersangkutan masuk dan menggantinya di menu <strong>Akun</strong>.
            </p>
          </div>
        )}
      </Dialog>
      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        title="Tambah anggota tim"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Batal
            </Button>
            <Button type="submit" form="new-admin" variant="primary" loading={create.isPending}>
              Tambah
            </Button>
          </>
        }
      >
        <form id="new-admin" onSubmit={submit} style={{ display: "grid", gap: 16 }}>
          <FormGrid>
            <Field label="Nama">{(id) => <Input id={id} name="name" required />}</Field>
            <Field label="Email">{(id) => <Input id={id} name="email" type="email" required />}</Field>
            <Field label="Peran">
              {(id) => (
                <Select id={id} name="role" value={newRole} onChange={(event) => setNewRole(event.target.value as Role)}>
                  {Object.entries(ROLE_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {newRole === "client" && (
              <Field label="Proyek klien" hint="Akun klien hanya bisa membuka proyek ini.">
                {(id, describedBy) => (
                  <Select id={id} name="projectId" required aria-describedby={describedBy} defaultValue="">
                    <option value="" disabled>
                      Pilih proyek…
                    </option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            <Field label="Kata sandi awal" hint="Minimal 8 karakter. Minta yang bersangkutan menggantinya.">
              {(id, describedBy) => <Input id={id} name="password" type="password" minLength={8} required aria-describedby={describedBy} />}
            </Field>
          </FormGrid>
          {create.isError && <p style={{ color: "var(--critical-ink)" }}>{create.error.message}</p>}
        </form>
      </Dialog>
    </Card>
  );
}

const ACTION_LABEL: Record<string, string> = {
  "project.create": "membuat proyek",
  "project.update": "mengubah proyek",
  "knowledge.publish": "menerbitkan pengetahuan",
  "knowledge.import": "mengimpor pengetahuan",
  "knowledge.restore": "memulihkan versi",
  "saldo.topup": "mencatat top-up",
  "saldo.adjustment": "mengoreksi saldo",
  "saldo.refund": "mengembalikan saldo",
  "key.create": "membuat kunci",
  "key.update": "mengubah kunci",
  "key.revoke": "mencabut kunci",
  "tests.review": "menyelesaikan uji terima",
  "settings.billing": "mengubah tarif",
  "links.import": "mengimpor rujukan",
  "links.settings": "mengubah domain/feed Linkage",
  "links.feed": "menyinkron feed rujukan",
  "links.activate": "mengaktifkan rujukan",
  "links.deactivate": "menonaktifkan rujukan",
  "links.delete": "menghapus rujukan",
  "admin.create": "menambah anggota tim",
  "admin.update": "mengubah anggota tim",
  "admin.delete": "menghapus anggota tim",
  "admin.reset_password": "mereset kata sandi",
  "admin.password": "mengganti kata sandi sendiri",
  "project.archive": "mengarsipkan proyek",
  "project.restore": "memulihkan proyek dari arsip",
  "project.delete": "menghapus proyek permanen",
  "project.avatar": "mengunggah foto avatar",
  "project.avatar.remove": "menghapus foto avatar",
};

function AuditCard() {
  const audit = useAudit();
  return (
    <Card title="Jejak audit" description="200 aktivitas terakhir yang menyentuh uang, kunci, pengetahuan, atau tim." flush>
      <div className={table.wrap} style={{ maxHeight: 480 }}>
        <table className={table.table}>
          <thead>
            <tr>
              <th className={table.first}>Waktu</th>
              <th>Siapa</th>
              <th>Aktivitas</th>
              <th className={table.last}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {(audit.data ?? []).map((row) => (
              <tr key={row.id}>
                <td className={`${table.first} ${table.muted}`}>{when.dateTime(row.createdAt)}</td>
                <td>{row.adminName ?? "sistem"}</td>
                <td>
                  {ACTION_LABEL[row.action] ?? row.action}
                  {row.projectName && <span className={table.muted}> · {row.projectName}</span>}
                </td>
                <td className={`${table.last} ${table.muted}`}>
                  <code className={styles.detail}>{row.detail ? JSON.stringify(row.detail) : ""}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
